import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildFrames } from '../solutionPlayback';

// Slow enough to follow a robot across the board and think about why it stopped
// there, quick enough that a 10-move solution is not a sit-down.
const DEFAULT_STEP_MS = 700;

/**
 * Play a solver solution out on the board, one move at a time.
 *
 * The board it plays on is borrowed, not owned: `positions` is null whenever
 * nothing is playing, which is the caller's signal to go back to showing the
 * real game. That is why stopping needs no unwinding — the hook simply stops
 * answering and the board underneath was never touched.
 *
 * The last move is left up for one step before the board is handed back, so a
 * solution does not vanish the instant it completes.
 */
export default function useSolutionPlayback({ walls, stepMs = DEFAULT_STEP_MS } = {}) {
  const [frames, setFrames] = useState([]);
  const [index, setIndex] = useState(0);

  const stop = useCallback(() => {
    setFrames([]);
    setIndex(0);
  }, []);

  const play = useCallback((moves, boardState) => {
    const built = buildFrames(boardState, walls, moves);
    // Nothing to show beats an empty playback that has to be stopped by hand.
    if (!built.length) return;
    setFrames(built);
    setIndex(0);
  }, [walls]);

  useEffect(() => {
    if (!frames.length) return undefined;
    const onLastMove = index >= frames.length - 1;
    const timer = setTimeout(() => {
      if (onLastMove) stop();
      else setIndex(i => i + 1);
    }, stepMs);
    return () => clearTimeout(timer);
  }, [frames, index, stepMs, stop]);

  // Trails are cumulative up to the move on screen, so scrubbing backwards (or
  // stopping early) can never leave a line drawn for a move nobody saw.
  const trails = useMemo(() => {
    const drawn = {};
    frames.slice(0, index + 1).forEach(({ color, from, to }) => {
      drawn[color] = [...(drawn[color] ?? []), { x: from.x, y: from.y }, { x: to.x, y: to.y }];
    });
    return drawn;
  }, [frames, index]);

  return {
    positions: frames.length ? frames[index].positions : null,
    progress: frames.length ? { step: index + 1, total: frames.length } : null,
    playing: frames.length > 0,
    trails,
    play,
    stop,
  };
}
